import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
      dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "retail-ecommerce-solution" },
      { name: "Real Estate", link: "/realestate-solution" },
      { name: "Logistics", link: "/logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🏗️",
    title: "Custom ERP Development",
    desc: "Fully bespoke ERP systems built around your exact business processes — no redundant modules, no forced workflows. Scales from 10 to 10,000 users.",
    tags: ["React", "Node.js", "PostgreSQL", "Microservices"],
    color: "#6366F1",
  },
  {
    icon: "🔶",
    title: "SAP Implementation & Customisation",
    desc: "End-to-end SAP S/4HANA, SAP Business One, and SAP ECC implementations with ABAP customisations, Fiori UX, and full go-live support.",
    tags: ["SAP S/4HANA", "SAP B1", "ABAP", "Fiori"],
    color: "#F97316",
  },
  {
    icon: "🟢",
    title: "Oracle ERP Cloud",
    desc: "Oracle Fusion Cloud ERP deployments covering Financials, Supply Chain, Manufacturing, and HCM with localised Indian compliance (GST, TDS).",
    tags: ["Oracle Fusion", "OAF", "PL/SQL", "BI Publisher"],
    color: "#EF4444",
  },
  {
    icon: "🔵",
    title: "Microsoft Dynamics 365",
    desc: "Dynamics 365 Finance, Supply Chain, Business Central, and Operations implementations with Power Platform extensions and Azure integrations.",
    tags: ["Dynamics 365", "Power Apps", "Azure", "D365 F&O"],
    color: "#0078D4",
  },
  {
    icon: "📦",
    title: "Inventory & Supply Chain",
    desc: "Real-time multi-warehouse inventory, demand forecasting, purchase order automation, vendor management, and logistics tracking modules.",
    tags: ["Warehouse Mgmt", "MRP", "Barcode", "RFID"],
    color: "#10B981",
  },
  {
    icon: "💰",
    title: "Finance & Accounting Module",
    desc: "GST-compliant accounts payable/receivable, general ledger, fixed assets, multi-currency, bank reconciliation, and automated financial reporting.",
    tags: ["GST", "TDS", "Tally Integration", "IFRS"],
    color: "#3B82F6",
  },
  {
    icon: "🏭",
    title: "Manufacturing & Production",
    desc: "Bill of materials, production planning, MRP/MRP II, shop floor control, quality management, and OEE dashboards for discrete and process manufacturing.",
    tags: ["BOM", "MRP II", "Shop Floor", "OEE"],
    color: "#8B5CF6",
  },
  {
    icon: "🔄",
    title: "ERP Migration & Integration",
    desc: "Pain-free migrations from legacy systems (Tally, Busy, custom Excel), plus API integrations with CRM, e-commerce, banking, and logistics platforms.",
    tags: ["ETL", "API Integration", "Tally", "Data Migration"],
    color: "#EC4899",
  },
];

const ERP_MODULES = [
  { icon: "💰", title: "Finance & Accounting", desc: "GL, AP/AR, cost centres, budgeting, GST filing", color: "#6366F1" },
  { icon: "📦", title: "Inventory Management", desc: "Multi-warehouse, batch/serial tracking, reorder alerts", color: "#10B981" },
  { icon: "🏭", title: "Manufacturing & MRP", desc: "BOM, work orders, production planning, quality control", color: "#8B5CF6" },
  { icon: "🛒", title: "Purchase Management", desc: "RFQ, PO approval workflows, vendor performance scoring", color: "#F59E0B" },
  { icon: "📊", title: "Sales & Order Management", desc: "Quotation → invoice, customer portals, delivery tracking", color: "#3B82F6" },
  { icon: "👥", title: "HRM & Payroll", desc: "Attendance, leave, payroll processing, PF/ESI compliance", color: "#EF4444" },
  { icon: "🔧", title: "Asset Management", desc: "Asset register, depreciation, maintenance schedules", color: "#EC4899" },
  { icon: "📈", title: "Business Intelligence", desc: "Real-time dashboards, KPI monitoring, management reports", color: "#06B6D4" },
];

const TECH_STACK = [
  {
    category: "ERP Platforms",
    items: [
      { name: "SAP S/4HANA", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#0070F3" },
      { name: "Oracle ERP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/oracle/oracle-original.svg", color: "#F80000" },
      { name: "MS Dynamics", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "Odoo", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#875A7B" },
      { name: "SAP Business One", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#00558A" },
      { name: "Custom ERP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#6366F1" },
    ],
  },
  {
    category: "Frontend",
    items: [
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Angular", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angularjs/angularjs-original.svg", color: "#DD0031" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg", color: "#42B883" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
    ],
  },
  {
    category: "Backend & Database",
    items: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "Java", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original.svg", color: "#007396" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg", color: "#4479A1" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
    ],
  },
  {
    category: "Cloud & DevOps",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "Azure", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
      { name: "GCP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg", color: "#4285F4" },
      { name: "GitHub", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg", color: "#181717" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Business Process Audit", desc: "We map every department's workflows, data flows, pain points, and integration requirements before recommending a single technology — no vendor-first bias.", color: "#6366F1" },
  { n: "02", icon: "🏗️", title: "Solution Architecture", desc: "Module selection, database design, integration architecture, role & permission matrix, and a phased rollout plan to minimise business disruption.", color: "#3B82F6" },
  { n: "03", icon: "⚙️", title: "Agile Development / Configuration", desc: "Bi-weekly sprints with a live staging ERP from day one. Department heads review real workflows — not static wireframes — throughout the build.", color: "#10B981" },
  { n: "04", icon: "🔗", title: "Data Migration & Integration", desc: "Master data cleansing, historical data migration, opening balance entry, and API integrations with banking, logistics, e-commerce, and payroll systems.", color: "#8B5CF6" },
  { n: "05", icon: "🧪", title: "UAT & Parallel Run", desc: "Structured user acceptance testing across all departments, a parallel run period where old and new systems run simultaneously to validate accuracy.", color: "#F59E0B" },
  { n: "06", icon: "🚀", title: "Go-Live & Hypercare", desc: "Zero-downtime cutover, on-site go-live support for the first week, 30-day hypercare with priority SLA, and comprehensive user training for every role.", color: "#EC4899" },
];

const CASE_STUDIES = [
  {
    title: "SSL Group ERP",
    industry: "Industrial Manufacturing",
    desc: "A pan-India industrial group replaced 8 legacy systems and 200+ spreadsheets with a unified custom ERP covering manufacturing, multi-warehouse inventory, purchase, finance, and a field sales app — integrated with SAP for consolidated reporting.",
    img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop&auto=format",
    tags: ["Custom React ERP", "SAP Integration", "Node.js", "PostgreSQL"],
    metrics: [{ val: "8", lbl: "Systems Replaced" }, { val: "60%", lbl: "Reporting Time Saved" }, { val: "₹1.2Cr", lbl: "Annual Cost Saving" }],
    color: "#6366F1",
  },
  {
    title: "Mtandt Equipment ERP",
    industry: "Equipment Rental & Logistics",
    desc: "A complete ERP for an equipment rental company covering asset tracking (GPS + RFID), rental contract management, maintenance scheduling, billing automation, and live fleet dashboards across 12 depots.",
    img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop&auto=format",
    tags: ["React", "Node.js", "GPS Integration", "IoT"],
    metrics: [{ val: "12", lbl: "Depots Unified" }, { val: "45%", lbl: "Asset Utilisation Up" }, { val: "Zero", lbl: "Manual Billing" }],
    color: "#10B981",
  },
  {
    title: "Columbus University ERP",
    industry: "Education",
    desc: "A full-stack academic ERP for a multi-campus university covering student information, admissions, fee management, timetabling, examination, library, hostel, and a parent-facing mobile app — replacing 6 standalone systems.",
    img: "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=600&h=400&fit=crop&auto=format",
    tags: ["Next.js", "Node.js", "PostgreSQL", "Flutter"],
    metrics: [{ val: "6", lbl: "Systems Replaced" }, { val: "15k+", lbl: "Students Onboarded" }, { val: "99.9%", lbl: "Uptime" }],
    color: "#3B82F6",
  },
];

const FAQS = [
  { q: "Should we build a custom ERP or implement SAP / Oracle?", a: "Off-the-shelf platforms like SAP Business One, Oracle NetSuite, or Microsoft Dynamics 365 are ideal for businesses with standard processes that fit within common industry templates. They offer faster deployment and proven reliability. Custom ERP development makes sense when your processes are genuinely unique, you need deep integration with proprietary systems, or you find platform licensing costs prohibitive at your scale. We'll give you an honest recommendation after a free process audit." },
  { q: "How long does an ERP implementation take?", a: "A focused single-module ERP (e.g. inventory + purchase) takes 3–5 months. A full-company ERP covering finance, inventory, manufacturing, and HR typically takes 6–12 months. Enterprise SAP or Oracle implementations can take 12–24 months. Our phased approach lets you go live with critical modules first — usually within 4–6 months — while other modules are completed in parallel." },
  { q: "How do you handle data migration from our current system?", a: "We've migrated from Tally, Busy, Marg, Excel, legacy ERPs, and paper-based systems. Our ETL process includes a full data audit, field mapping, deduplication, validation rules, master data cleansing, and a dry-run migration in a sandbox before production cutover. We also handle opening balance entry and historical transaction migration. You'll see a data quality report before anything goes live." },
  { q: "Will the ERP work on mobile for our field teams?", a: "Yes. Every ERP we build includes a responsive web interface and optional native iOS/Android apps for field-facing roles — sales, delivery, maintenance, and site supervisors. Mobile apps support offline-first operation with intelligent sync, so they work even in low-connectivity environments like factory floors and remote depots." },
  { q: "How do you ensure GST and Indian compliance?", a: "All custom ERPs we build include built-in GST compliance — GSTIN management, HSN/SAC codes, GST invoice generation, GSTR-1/2A/3B reports, e-way bill integration, and TDS/TCS handling. For clients on SAP or Oracle, we implement localisation patches and configure Indian tax procedures. We stay current with GST council updates and push compliance patches proactively." },
  { q: "What post-go-live support do you provide?", a: "All ERP projects include 30 days of free post-go-live hypercare. Beyond that, we offer SLA-backed annual maintenance contracts covering bug fixes, regulatory compliance updates (GST changes, new RBI/MCA requirements), performance tuning, user additions, and minor feature enhancements. Our average ERP client has been with us for 4.2 years post-go-live." },
];

const STATS = [
  { val: "150+", lbl: "ERP Projects Delivered", icon: "🏭" },
  { val: "8+", lbl: "Industries Served", icon: "🌐" },
  { val: "60%", lbl: "Avg Operational Cost Saved", icon: "💰" },
  { val: "4.2 Yrs", lbl: "Avg Client Relationship", icon: "🤝" },
];

const INDUSTRIES = [
  { icon: "🏭", name: "Manufacturing", desc: "Discrete & process manufacturing", color: "#6366F1" },
  { icon: "🛒", name: "Retail & Distribution", desc: "Multi-branch inventory & sales", color: "#10B981" },
  { icon: "🏥", name: "Healthcare", desc: "Hospital & pharma management", color: "#3B82F6" },
  { icon: "🎓", name: "Education", desc: "Academic & campus management", color: "#8B5CF6" },
  { icon: "🏗️", name: "Construction", desc: "Project costing & material tracking", color: "#F59E0B" },
  { icon: "🚚", name: "Logistics", desc: "Fleet, depot & contract management", color: "#EF4444" },
  { icon: "🏨", name: "Hospitality", desc: "Hotel & restaurant management", color: "#EC4899" },
  { icon: "🔧", name: "Field Services", desc: "Job cards, assets & maintenance", color: "#06B6D4" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Smart logic to check which page we are currently on
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            // Check if this specific main link is the active page
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());

            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    // Highlight the "Services" button if we are on the web-development page
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        // Highlight "Web Development" inside the dropdown specifically
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="/#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
          }}>☰</button>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ paddingBottom: 14, borderBottom: "1px solid #F3F4F6", marginBottom: 4 }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          {NAV_LINKS.map(l => (
            <a key={l.label} href={l.link || "#"} style={{ padding: "10px 0", color: currentPath.includes(l.label.toLowerCase()) ? "#3B82F6" : "#374151", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #F3F4F6", fontFamily: "'Sora',sans-serif" }}>{l.label}</a>
          ))}
          <a href="/#contact" style={{ marginTop: 12, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", padding: "13px", borderRadius: 8, textAlign: "center", fontWeight: 700, textDecoration: "none" }}>Get Free Consultation</a>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [activeMetric, setActiveMetric] = useState(0);
  const metrics = ["60% Operational Cost Saved", "8 Systems into 1", "Real-Time Business Visibility", "100% GST Compliant"];

  useEffect(() => {
    const t = setInterval(() => setActiveMetric(p => (p + 1) % metrics.length), 2500);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 96,
      background: "linear-gradient(160deg,#EEF2FF 0%,#F0FDF4 50%,#EEF2FF 100%)",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -80, right: -80, width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(99,102,241,.12),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -80, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(16,185,129,.08),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(99,102,241,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,.04) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#6B7280" }}>
          <Link to="/" style={{ color: "#6366F1", textDecoration: "none", fontWeight: 600 }}>Home</Link>
          <span>›</span>
          <span>Solutions</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600 }}>ERP Solution</span>
        </div>

        <div className="erp-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 99, padding: "6px 16px", marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366F1", display: "inline-block", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#3730A3" }}>🏭 India's Leading ERP Development Company</span>
            </div>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(34px,4.5vw,58px)", fontWeight: 900, color: "#0F172A", lineHeight: 1.1, marginBottom: 20 }}>
              One ERP. Every<br />
              <span style={{ background: "linear-gradient(135deg,#4338CA,#6366F1,#10B981)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Department. Zero Chaos.
              </span>
            </h1>
            <p style={{ fontSize: 17, color: "#6B7280", lineHeight: 1.8, maxWidth: 560, marginBottom: 28 }}>
              We replace your maze of spreadsheets and disconnected software with a single, unified ERP that gives every department real-time visibility and automates the workflows that eat your team's time.
            </p>

            {/* Live metric ticker */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#fff", border: "1.5px solid #C7D2FE", borderRadius: 10, padding: "10px 18px", marginBottom: 36 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981", display: "inline-block", animation: "pulse 1.5s infinite", flexShrink: 0 }} />
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#374151" }}>Our clients achieve: </span>
              <span style={{ fontSize: 13.5, fontWeight: 800, color: "#6366F1" }}>{metrics[activeMetric]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 44 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#4338CA,#6366F1)", color: "#fff",
                padding: "15px 30px", borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 6px 20px rgba(99,102,241,.4)",
                transition: "transform .2s,box-shadow .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 28px rgba(99,102,241,.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(99,102,241,.4)"; }}
              >🚀 Build My ERP</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#4338CA", padding: "15px 30px", borderRadius: 10,
                fontWeight: 700, fontSize: 15, textDecoration: "none", border: "2px solid #C7D2FE",
                transition: "all .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#EEF2FF"; e.currentTarget.style.borderColor = "#A5B4FC"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#C7D2FE"; }}
              >📂 View Case Studies</a>
            </div>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {[["150+", "ERP Projects"], ["60%", "Cost Saved"], ["4.2 Yrs", "Avg Relationship"], ["8+", "Industries"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#4338CA" }}>{v}</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 600 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — ERP Dashboard Visual */}
          <div style={{ position: "relative" }}>
            <div style={{ borderRadius: 24, overflow: "hidden", boxShadow: "0 40px 100px rgba(99,102,241,.2)", border: "1px solid #E5E7EB", background: "#fff" }}>
              {/* App chrome */}
              <div style={{ background: "#1E1B4B", padding: "14px 20px", display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {["#EF4444","#F59E0B","#10B981"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
                </div>
                <div style={{ flex: 1, background: "rgba(255,255,255,.1)", borderRadius: 6, padding: "5px 12px", fontSize: 12, color: "rgba(255,255,255,.7)", fontWeight: 500 }}>
                  🔒 erp.yourcompany.com
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", fontWeight: 600 }}>v3.2.1</div>
              </div>
              {/* Sidebar + Main */}
              <div style={{ display: "flex", minHeight: 380 }}>
                {/* Sidebar */}
                <div style={{ width: 52, background: "#1E1B4B", display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0", gap: 20 }}>
                  {["🏠","💰","📦","🏭","🛒","👥","📊","⚙️"].map((icon, i) => (
                    <div key={i} style={{ width: 36, height: 36, borderRadius: 8, background: i === 3 ? "rgba(99,102,241,.4)" : "rgba(255,255,255,.05)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, cursor: "pointer" }}>{icon}</div>
                  ))}
                </div>
                {/* Main panel */}
                <div style={{ flex: 1, padding: "16px 14px", background: "#F8FAFC", overflowX: "auto" }}>
                  {/* Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 14, color: "#0F172A" }}>Manufacturing Dashboard</div>
                    <div style={{ fontSize: 11, background: "#EEF2FF", color: "#6366F1", padding: "3px 10px", borderRadius: 99, fontWeight: 700 }}>Live ●</div>
                  </div>
                  {/* KPI row */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 12 }}>
                    {[
                      ["₹4.8Cr", "Monthly Revenue", "#6366F1"],
                      ["98.2%", "Production OEE", "#10B981"],
                      ["1,240", "Open Orders", "#F59E0B"],
                      ["₹12.4L", "Pending Payables", "#EF4444"],
                    ].map(([v, l, c]) => (
                      <div key={l} style={{ background: "#fff", borderRadius: 10, padding: "10px 10px", border: `1px solid ${c}20` }}>
                        <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 14, color: c }}>{v}</div>
                        <div style={{ fontSize: 9, color: "#9CA3AF", fontWeight: 600, marginTop: 2, lineHeight: 1.3 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                  {/* Production table */}
                  <div style={{ background: "#fff", borderRadius: 10, padding: "10px", border: "1px solid #E5E7EB", marginBottom: 10 }}>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 11, color: "#374151", marginBottom: 8 }}>Work Orders — Today</div>
                    {[
                      ["WO-2847", "Steel Frame A4", "In Progress", "78%", "#6366F1"],
                      ["WO-2848", "Hydraulic Pump", "Completed", "100%", "#10B981"],
                      ["WO-2849", "Control Panel", "Pending", "0%", "#F59E0B"],
                    ].map(([id, name, status, pct, c]) => (
                      <div key={id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #F3F4F6", fontSize: 10 }}>
                        <span style={{ fontWeight: 700, color: "#374151", width: 60 }}>{id}</span>
                        <span style={{ color: "#6B7280", flex: 1, paddingLeft: 8 }}>{name}</span>
                        <span style={{ color: c, fontWeight: 700, background: `${c}12`, padding: "2px 8px", borderRadius: 99 }}>{status}</span>
                        <span style={{ color: c, fontWeight: 800, width: 36, textAlign: "right" }}>{pct}</span>
                      </div>
                    ))}
                  </div>
                  {/* Stock alerts */}
                  <div style={{ background: "#FEF2F2", borderRadius: 8, padding: "8px 10px", border: "1px solid #FECACA", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14 }}>⚠️</span>
                    <span style={{ fontSize: 10, color: "#991B1B", fontWeight: 600 }}>3 items below reorder level — Auto PO draft created</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating cards */}
            <div style={{ position: "absolute", top: 24, left: -20, background: "#fff", borderRadius: 16, padding: "14px 18px", boxShadow: "0 12px 40px rgba(99,102,241,.2)", border: "1.5px solid #C7D2FE", animation: "float 3s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#4338CA" }}>8 → 1</div>
              <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500, marginTop: 2 }}>Systems Unified 🔗</div>
            </div>
            <div style={{ position: "absolute", bottom: 60, right: -20, background: "linear-gradient(135deg,#4338CA,#6366F1)", borderRadius: 16, padding: "13px 18px", boxShadow: "0 12px 36px rgba(99,102,241,.4)", animation: "float 3.5s ease-in-out infinite .5s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 20, color: "#fff", lineHeight: 1 }}>60%</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 500, marginTop: 3 }}>Cost Saved 💰</div>
            </div>
            <div style={{ position: "absolute", top: 110, right: -16, background: "#fff", borderRadius: 14, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.1)", border: "1.5px solid #D1FAE5", animation: "float 4s ease-in-out infinite 1s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,#10B981,#059669)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>✓</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 12, color: "#065F46", fontFamily: "'Sora',sans-serif" }}>GST Filed!</div>
                  <div style={{ fontSize: 10, color: "#6B7280" }}>GSTR-1 • Auto-generated</div>
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", bottom: 20, left: -16, background: "#fff", borderRadius: 14, padding: "11px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.08)", border: "1px solid #E5E7EB", animation: "float 3.8s ease-in-out infinite .3s", zIndex: 10 }}>
              <div style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 700, marginBottom: 5, textTransform: "uppercase" }}>Integrated with</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["💳","📧","🏦","📦","🚚"].map((e, i) => <span key={i} style={{ fontSize: 15 }}>{e}</span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#0F172A", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 32, textAlign: "center" }} className="erp-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,3vw,40px)", fontWeight: 900, color: "#fff", lineHeight: 1 }}>{s.val}</div>
            <div style={{ color: "#64748B", fontSize: 13, marginTop: 6, fontWeight: 500 }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── ERP Modules ───────────────────────────────────────────────────────────────
function ErpModules() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#fff" }} className="erp-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Core Modules</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Every Module Your Business Needs</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            Our ERP solutions cover every function — choose the modules you need now and add more as you grow.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 20 }}>
          {ERP_MODULES.map((m, i) => (
            <div key={m.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                padding: "28px 24px", borderRadius: 18,
                border: `2px solid ${hov === i ? m.color : "#F1F5F9"}`,
                background: hov === i ? `${m.color}08` : "#FAFAFA",
                boxShadow: hov === i ? `0 16px 48px ${m.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .28s", transform: hov === i ? "translateY(-5px)" : "none", cursor: "default",
              }}>
              <div style={{ fontSize: 32, marginBottom: 14 }}>{m.icon}</div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, color: "#0F172A", marginBottom: 6 }}>{m.title}</h3>
              <p style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6 }}>{m.desc}</p>
              <div style={{ marginTop: 14, height: 3, borderRadius: 99, background: `linear-gradient(90deg,${m.color},${m.color}40)`, transform: hov === i ? "scaleX(1)" : "scaleX(0.3)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Industries ────────────────────────────────────────────────────────────────
function Industries() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="erp-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Industries</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>ERP for Every Industry</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Deep domain expertise across 8+ verticals — we speak your industry's language and understand your compliance requirements.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 20 }}>
          {INDUSTRIES.map((ind, i) => (
            <div key={ind.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 18, padding: "28px 24px",
                border: `2px solid ${hov === i ? ind.color : "#E5E7EB"}`,
                boxShadow: hov === i ? `0 12px 40px ${ind.color}18` : "none",
                transition: "all .25s", transform: hov === i ? "translateY(-4px)" : "none", cursor: "default",
              }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>{ind.icon}</div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: "#0F172A", marginBottom: 6 }}>{ind.name}</h3>
              <p style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.5 }}>{ind.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "96px 40px", background: "#fff" }} className="erp-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Services</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>ERP Development & Implementation Services</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            From a lean custom ERP to an enterprise SAP implementation — we deliver at every scale and every budget.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? s.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 24px 64px ${s.color}22` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s ease", transform: hov === i ? "translateY(-6px)" : "none",
              }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${s.color}25` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, marginBottom: 18 }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}12`, color: s.color, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, border: `1px solid ${s.color}25` }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .2s" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const points = [
    { icon: "🗺️", title: "Process-First Approach", desc: "We map every business process before recommending technology. No vendor bias — we pick the right platform (or build custom) based solely on your needs.", color: "#6366F1" },
    { icon: "🇮🇳", title: "India Compliance Built-In", desc: "GST (GSTR-1/3B/e-way bill), TDS/TCS, PF/ESI, Companies Act reporting — all built into every ERP we deliver, updated proactively as regulations change.", color: "#10B981" },
    { icon: "🔗", title: "Total Integration Capability", desc: "Banks, payment gateways, CRM, e-commerce, logistics APIs, Tally/SAP bridges — we've integrated with 100+ external systems across our 150+ ERP projects.", color: "#3B82F6" },
    { icon: "📱", title: "Mobile-Ready from Day One", desc: "Every ERP includes responsive web access plus optional native iOS/Android apps for field teams, approvers, and executives — including offline-first operation.", color: "#8B5CF6" },
    { icon: "🔒", title: "Enterprise-Grade Security", desc: "Role-based access down to field level, full audit trails, AES-256 encryption, 2FA, IP whitelisting, and annual security audits. ISO 27001-aligned practices.", color: "#EF4444" },
    { icon: "📞", title: "4.2-Year Avg Relationship", desc: "We're not a set-it-and-forget-it shop. Our average ERP client has been with us for 4.2 years post-go-live — with quarterly reviews and proactive optimisation.", color: "#EC4899" },
  ];
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="erp-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>The ERP Partner That Stays With You</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Why 150+ businesses chose Techvanto to unify their operations.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
          {points.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? p.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: 20,
              }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${p.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#0F172A", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.7 }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "96px 40px", background: "#fff" }} className="erp-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Tech Stack</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Platforms & Technologies We Master</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>From SAP S/4HANA to fully custom cloud-native ERPs — we work across every major ERP platform and the modern tech stack that powers them.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s", fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#4338CA,#6366F1)" : "#fff",
              borderColor: active === i ? "#6366F1" : "#E5E7EB",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(99,102,241,.35)" : "none",
            }}>{t.category}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 20,
                border: `2px solid ${hov === i ? tech.color : "#F1F5F9"}`,
                background: hov === i ? `${tech.color}08` : "#FAFAFA",
                boxShadow: hov === i ? `0 12px 36px ${tech.color}28` : "0 1px 4px rgba(0,0,0,.04)",
                transition: "all .28s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-6px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}15` : "#fff", border: `1px solid ${hov === i ? tech.color + "30" : "#E5E7EB"}`, transition: "all .28s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .28s", transform: hov === i ? "scale(1.1)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 2 }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 18px 18px", background: `linear-gradient(90deg,${tech.color},${tech.color}80)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "96px 40px", background: "linear-gradient(160deg,#EEF2FF 0%,#F0FDF4 100%)" }} className="erp-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>From Business Chaos to Operational Clarity</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>A structured 6-stage methodology that has delivered 150+ ERP projects on time and on budget.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 20, padding: "32px 28px",
              border: "1px solid #E5E7EB", boxShadow: "0 4px 20px rgba(0,0,0,.05)",
              position: "relative", overflow: "hidden", transition: "all .3s",
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 20px 60px ${step.color}18`; e.currentTarget.style.borderColor = `${step.color}40`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,.05)"; e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}12`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${step.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${step.color}20` }}>
                {step.icon}
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}15`, padding: "3px 10px", borderRadius: 99 }}>STEP {step.n}</span>
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#0F172A", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75 }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}50)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "96px 40px", background: "#fff" }} className="erp-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>ERP Transformations We're Proud Of</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Real businesses, real results — ERP implementations that transformed operations across industries.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 60 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 52, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="erp-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}25` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 360, objectFit: "cover", display: "block", transition: "transform .5s", transform: hov === cs.title ? "scale(1.04)" : "scale(1)" }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 99 }}>{cs.industry}</div>
              </div>
              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,36px)", fontWeight: 900, color: "#0F172A", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 28 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}12`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 99, border: `1px solid ${cs.color}25` }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 28, marginBottom: 32, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 26, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 4 }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff", padding: "13px 26px", borderRadius: 10,
                  fontWeight: 700, fontSize: 14, textDecoration: "none",
                  boxShadow: `0 6px 20px ${cs.color}35`, transition: "transform .2s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "none"}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#4338CA", border: "2px solid #C7D2FE",
            padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .2s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#EEF2FF"; e.currentTarget.style.borderColor = "#A5B4FC"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#C7D2FE"; }}
          >View All Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="erp-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16 }}>Clear answers before you start your ERP journey.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 16,
              border: `2px solid ${open === i ? "#6366F1" : "#E5E7EB"}`,
              overflow: "hidden", transition: "border-color .25s",
              boxShadow: open === i ? "0 8px 32px rgba(99,102,241,.1)" : "none",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 28px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 700, fontSize: 15.5, color: "#0F172A", textAlign: "left", lineHeight: 1.4, flex: 1, marginRight: 16 }}>{faq.q}</span>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: open === i ? "#6366F1" : "#F1F5F9",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .25s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#6B7280", fontSize: 18, lineHeight: 1, transition: "transform .25s", display: "block", transform: open === i ? "rotate(45deg)" : "none" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 28px 24px", animation: "fadeIn .2s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.8, borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", desc: "" });
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600); };

  const iStyle = (n) => ({
    width: "100%", padding: "13px 16px", borderRadius: 10, fontSize: 14,
    border: `2px solid ${focused === n ? "#6366F1" : "#E5E7EB"}`,
    background: focused === n ? "#EEF2FF" : "#FAFAFA",
    color: "#0F172A", outline: "none", fontFamily: "'Sora',sans-serif",
    transition: "all .2s", boxShadow: focused === n ? "0 0 0 4px rgba(99,102,241,.1)" : "none",
  });

  return (
    <section id="contact" style={{ padding: "96px 40px", background: "linear-gradient(160deg,#EEF2FF,#F0FDF4)" }} className="erp-section">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366F1", letterSpacing: ".1em", textTransform: "uppercase" }}>Get Started</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 14 }}>Let's Unify Your Business Operations</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.75 }}>Tell us about your business and current systems — get a free ERP audit + proposal within 24 hours.</p>
        </div>
        <div className="erp-contact-grid">
          {/* Info panel */}
          <div>
            <div style={{ background: "linear-gradient(135deg,#3730A3,#4338CA,#6366F1)", borderRadius: 24, padding: "40px 36px", marginBottom: 24, boxShadow: "0 20px 60px rgba(99,102,241,.3)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 8, position: "relative" }}>Free ERP Business Audit</h3>
              <p style={{ color: "rgba(255,255,255,.75)", fontSize: 14, lineHeight: 1.7, marginBottom: 32, position: "relative" }}>We'll audit your current systems, identify the biggest operational bottlenecks, and recommend an ERP strategy with an honest ROI projection.</p>
              {[
                { icon: "📞", label: "Phone", val: "+91 96462 06032" },
                { icon: "✉️", label: "Email", val: "info@techvanto.com" },
                { icon: "📍", label: "Location", val: "New Delhi, India 🇮🇳" },
                { icon: "⏰", label: "Hours", val: "Mon–Sat, 9AM – 7PM IST" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600 }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 20, padding: "28px", border: "1px solid #E5E7EB", boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0F172A", marginBottom: 18 }}>What You Get — Free</h4>
              {[
                ["🔍", "Current systems & process bottleneck audit"],
                ["🏗️", "ERP architecture recommendation"],
                ["💰", "Transparent fixed-price quote with ROI projection"],
                ["🇮🇳", "GST & compliance assessment"],
                ["🔒", "NDA signed before any discussion"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                  <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500, lineHeight: 1.5 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 24, padding: "44px", boxShadow: "0 8px 48px rgba(0,0,0,.08)", border: "1px solid #F1F5F9" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: "linear-gradient(135deg,#6366F1,#4338CA)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 40, boxShadow: "0 12px 36px rgba(99,102,241,.35)" }}>🏭</div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: "#0F172A", marginBottom: 12 }}>Request Received! 🎉</h3>
                <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.7, maxWidth: 320, margin: "0 auto 28px" }}>Our ERP specialists will review your business details and reach out within 24 hours with a free audit and proposal.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", company: "", desc: "" }); }} style={{ background: "linear-gradient(135deg,#4338CA,#6366F1)", color: "#fff", border: "none", padding: "13px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Sora',sans-serif" }}>Submit Another Request</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#0F172A", marginBottom: 6 }}>Tell Us About Your Business</h3>
                <p style={{ color: "#9CA3AF", fontSize: 13.5, marginBottom: 32 }}>The more context you provide, the better our ERP recommendation will be.</p>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="erp-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" required style={iStyle("name")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="name@company.com" required style={iStyle("email")} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="erp-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Phone *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="+91 XXXXX XXXXX" required style={iStyle("phone")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Company & Industry</label>
                      <input name="company" value={form.company} onChange={handleChange} onFocus={() => setFocused("company")} onBlur={() => setFocused(null)} placeholder="e.g. ABC Mfg — Manufacturing" style={iStyle("company")} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Describe Your ERP Requirements *</label>
                    <textarea name="desc" value={form.desc} onChange={handleChange} onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)} placeholder="What systems do you currently use? What departments do you want to unify? How many users will the ERP have? What are your biggest operational pain points? Any preferred platform (SAP, Oracle, custom)?" rows={5} required style={{ ...iStyle("desc"), resize: "vertical", minHeight: 130, lineHeight: 1.7 }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>🔒 Your information is 100% secure and kept strictly confidential. NDA available on request.</p>
                  <button type="submit" disabled={loading} style={{
                    width: "100%", padding: "15px", borderRadius: 12,
                    background: loading ? "#A5B4FC" : "linear-gradient(135deg,#4338CA,#6366F1)",
                    color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 800, fontSize: 16, fontFamily: "'Sora',sans-serif",
                    boxShadow: loading ? "none" : "0 8px 24px rgba(99,102,241,.4)",
                    transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  }}>
                    {loading ? "Submitting…" : "🏭 Get Free ERP Audit & Proposal"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Services", links: ["Web Development", "Mobile Apps", "AI & ML", "Cloud & DevOps", "UI/UX Design", "E-Commerce"] },
    { title: "Solutions", links: ["CRM Solution", "ERP Solution", "HRM Solution", "LMS Solution", "POS Solution", "SaaS Platform"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Laravel", "AWS & GCP", "Docker & K8s", "SAP & Oracle"] },
  ];
  return (
    <footer style={{ background: "#0F172A", color: "#fff", padding: "72px 40px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ marginBottom: 56 }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: 140, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.75, fontSize: 14, maxWidth: 300, marginBottom: 24 }}>
              Building world-class digital products for businesses across 20+ countries. Innovation. Scale. Excellence.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#6366F1"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, letterSpacing: ".04em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s" }}
                  onMouseEnter={e => e.target.style.color = "#A5B4FC"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid #1E293B", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13 }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13 }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function ErpSolutions() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:.4; } }

        .nav-links         { display:flex; align-items:center; gap:4px; }
        .nav-cta           { display:flex !important; }
        .mob-menu-btn      { display:none; }

        .erp-hero-grid     { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .erp-stats-grid    { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; }
        .erp-cs-grid       { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .erp-contact-grid  { display:grid; grid-template-columns:1fr 1.5fr; gap:48px; align-items:start; }
        .footer-grid       { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }
        .erp-form-row      { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        @media (max-width:1100px) {
          .erp-hero-grid  { gap:40px; }
          .footer-grid    { grid-template-columns:1fr 1fr; gap:32px; }
          .erp-stats-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:900px) {
          .erp-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .erp-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .erp-contact-grid { grid-template-columns:1fr; gap:32px; }
          .footer-grid      { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links        { display:none !important; }
          .nav-cta          { display:none !important; }
          .mob-menu-btn     { display:flex !important; }
        }
        @media (max-width:640px) {
          .footer-grid    { grid-template-columns:1fr; }
          .erp-stats-grid { grid-template-columns:repeat(2,1fr); gap:24px; }
          .erp-form-row   { grid-template-columns:1fr !important; }
        }
        @media (max-width:500px) { .topbar-right { display:none !important; } }
        @media (max-width:900px)  { .erp-section { padding-left:20px !important; padding-right:20px !important; } }
        @media (max-width:640px)  { .erp-section { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; } }
      `}</style>

      <Hero />
      <StatsBar />
      <ErpModules />
      <Industries />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}